import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types } from 'mobx-state-tree';
import { api } from 'src/api';
import { createAsync } from 'src/utils/asyncModel';

/** The status endpoint refuses more ids than this in one request. */
const MAX_STATUS_BILL_IDS = 500;

/** The ids come from different services and may differ in case, so the map is keyed in one of them. */
const keyOf = billId => String(billId).toLowerCase();
const distinctIds = billIds => {
  const seen = new Set();
  return billIds.filter(billId => {
    if (!billId || seen.has(keyOf(billId))) return false;
    seen.add(keyOf(billId));
    return true;
  });
};

/**
 * Reads push status for a whole page of bills at once, and is the only caller of the status endpoint
 * on the Bills page. `createAsync` cancels the previous call on the same action, so a second reader —
 * the details modal asking about the one bill it shows, say — would silently cancel the table's read.
 * One page holds at most 100 bills against a cap of 500, so a page always fits in a single call.
 */
const readPushStatusesAsync = createAsync(body => {
  return async function readPushStatuses(options) {
    var _resp$response$status, _resp$response;
    const billIds = distinctIds(body.billIds).slice(0, MAX_STATUS_BILL_IDS);
    if (!billIds.length) return undefined;
    const resp = await api.BillAutomationBills.getAppfolioPushStatus({
      billIds
    }, options);
    if (resp !== null && resp !== void 0 && resp.isSuccessful) this.setStatuses(billIds, (_resp$response$status = (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.statuses) !== null && _resp$response$status !== void 0 ? _resp$response$status : []);
    return resp;
  };
});

/**
 * Queues one document's payable. Answers as soon as it is recorded — the outcome arrives on the next
 * status read, never by waiting on this call.
 */
const pushBillAsync = createAsync(body => {
  return async function pushBill(options) {
    // The body goes in the middle and the cancel token last. Passing `options` positionally as the body
    // left the third parameter unset, and the request threw on `options.cancelToken` before anything
    // went out — see the shape assertion in useBillsAppfolioPush.test.js.
    return api.BillAutomationBills.pushBillToAppfolio(body.billId, body.invoiceNumber ? {
      invoiceNumber: body.invoiceNumber
    } : undefined, options);
  };
});

/** AppFolio AP push state for the bills the Bills page currently lists. */
const AppfolioPushStore = types.model('AppfolioPushStore', {
  statusByBillId: types.optional(types.frozen(), {}),
  /**
   * The bills a read has actually covered. A document the caller's company does not own is omitted
   * from the response entirely, and that is not the same answer as "not pushed" — without this the
   * two are indistinguishable.
   */
  readBillIds: types.optional(types.frozen(), []),
  pushBillAsync,
  readPushStatusesAsync
}).actions(store => ({
  setStatuses(requestedBillIds, statuses) {
    const next = _objectSpread({}, store.statusByBillId);

    // Whatever the read covered is restated by it, so a bill it omitted drops rather than going stale.
    requestedBillIds.forEach(billId => delete next[keyOf(billId)]);
    statuses.forEach(status => {
      next[keyOf(status.billId)] = status;
    });
    store.statusByBillId = next;
    store.readBillIds = [...new Set([...store.readBillIds, ...requestedBillIds.map(keyOf)])];
  },
  /** What a trigger answered, so the row settles now rather than at the next read. */
  setStatus(status) {
    const key = keyOf(status.billId);
    store.statusByBillId = _objectSpread(_objectSpread({}, store.statusByBillId), {}, {
      [key]: status
    });
    if (!store.readBillIds.includes(key)) store.readBillIds = [...store.readBillIds, key];
  },
  /**
   * Drops what was read about one bill, because it has just been overtaken. A push read before the
   * trigger says the document was never pushed, and that answer outlives the trigger unless it is
   * thrown away — leaving a view showing "not pushed" about a document now on its way to AppFolio.
   */
  forget(billId) {
    const key = keyOf(billId);
    if (!(key in store.statusByBillId)) return;
    const remaining = _objectSpread({}, store.statusByBillId);
    delete remaining[key];
    store.statusByBillId = remaining;
    store.readBillIds = store.readBillIds.filter(id => id !== key);
  }
})).views(store => ({
  /** Null both while a bill has not been read and when the read omitted it — `hasReadStatus` tells them apart. */
  getStatus(billId) {
    var _store$statusByBillId;
    return (_store$statusByBillId = store.statusByBillId[keyOf(billId)]) !== null && _store$statusByBillId !== void 0 ? _store$statusByBillId : null;
  },
  hasReadStatus(billId) {
    return store.readBillIds.includes(keyOf(billId));
  }
}));
export const appfolioPush = types.optional(AppfolioPushStore, {});